<template>
  <div class="bullet-editor">
    <div class="editor-head">
      <div>
        <label>Bullets <span class="required">*</span></label>
        <p class="hint">
          Up to {{ MAX_BULLETS }} bullets. Each one needs an icon image
          (Apple requires every bullet to carry an image).
        </p>
      </div>
      <span class="count-badge">{{ value.length }} / {{ MAX_BULLETS }}</span>
    </div>

    <div class="rows">
      <retention-bullet-row
        v-for="(bullet, idx) in value"
        :key="idx"
        :bullet="bullet"
        :index="idx"
        :total="value.length"
        :disabled="disabled"
        @update="onUpdate"
        @remove="onRemove"
        @move-up="onMoveUp"
        @move-down="onMoveDown"
      />
    </div>

    <button
      type="button"
      class="add-btn"
      :disabled="disabled || value.length >= MAX_BULLETS"
      @click="onAdd"
    >
      <i class="fas fa-plus"></i>
      Add bullet point
      <span v-if="value.length >= MAX_BULLETS" class="add-btn-note">(max reached)</span>
    </button>
  </div>
</template>

<script>
import RetentionBulletRow from './BulletRow.vue';
import { MAX_BULLETS } from '../../constants/retentionMessageTypes';

const blankBullet = () => ({ text: '', altText: '', imageIdentifier: '' });

export default {
  name: 'RetentionMessageBulletEditor',
  components: { RetentionBulletRow },
  props: {
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { MAX_BULLETS };
  },
  mounted() {
    // Always render at least one row so the user has something to fill in.
    if (!this.disabled && this.value.length === 0) {
      this.$emit('input', [blankBullet()]);
    }
  },
  methods: {
    onUpdate(index, bullet) {
      const next = [...this.value];
      next[index] = bullet;
      this.$emit('input', next);
    },
    onRemove(index) {
      if (this.value.length <= 1) return;
      const next = this.value.filter((_, i) => i !== index);
      this.$emit('input', next);
    },
    onAdd() {
      if (this.value.length >= MAX_BULLETS) return;
      this.$emit('input', [...this.value, blankBullet()]);
    },
    onMoveUp(index) {
      if (index === 0) return;
      const next = [...this.value];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      this.$emit('input', next);
    },
    onMoveDown(index) {
      if (index >= this.value.length - 1) return;
      const next = [...this.value];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      this.$emit('input', next);
    }
  }
};
</script>

<style scoped>
.bullet-editor {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.editor-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
  margin: 0;
  letter-spacing: 0.02em;
}

.required {
  color: #ff6b6b;
}

.hint {
  margin: 0.3rem 0 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.4;
}

.count-badge {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 3px 9px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.add-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.95rem;
  background: rgba(3, 179, 218, 0.12);
  border: 1px dashed rgba(3, 179, 218, 0.4);
  border-radius: 6px;
  color: #6dd0ea;
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
}

.add-btn:hover:not(:disabled) {
  background: rgba(3, 179, 218, 0.22);
  border-color: #03B3DA;
  color: #fff;
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn-note {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 400;
  margin-left: 0.3rem;
}
</style>
